import type { OtherworldlyGift } from "../../types";

export const otherworldlyGifts: OtherworldlyGift[] = [
  // Unlimited gifts (repeatable: true)
  {
    id: "gift-of-choice",
    name: "Gift of Choice",
    description:
      "+1 MP. You may swap one of your Dark Offerings for another you do not already have.",
    unlimited: true,
    effects: [{ type: "mp", value: 1 }],
  },
  {
    id: "gift-of-destruction",
    name: "Gift of Destruction",
    description: "+1 MP. Gain 3 points to spend on Spells of Destruction.",
    unlimited: true,
    effects: [{ type: "mp", value: 1 }],
    spellPoints: { listId: "destruction", points: 3 },
  },
  {
    id: "gift-of-earthly-experience",
    name: "Gift of Earthly Experience",
    description:
      "Lose 1 MP. Gain 1 Core Skill point to spend on any Core Skill.",
    unlimited: true,
    effects: [
      { type: "mp", value: -1 },
      { type: "skillPoints", value: 1 },
    ],
  },
  {
    id: "gift-of-prosperity",
    name: "Gift of Prosperity",
    description:
      "+1 MP. Increase your Wealth Level by 1 (max 4). Once per day per purchase of this gift, you may gain 1 silver coin instead of 1 MP from a Patron Challenge.",
    full_description: [
      {
        type: "paragraph",
        text: "You gain +1 MP and +1 Wealth Level. In addition, once per day, you can choose to gain 1 silver coin instead of 1 MP whenever you complete a Patron Challenge.",
      },
      {
        type: "paragraph",
        text: "For each time this Gift is taken again, you may use this ability another time per day. This Gift cannot raise you to Wealth Level 5 (Royalty).",
      },
    ],
    unlimited: true,
    effects: [
      { type: "mp", value: 1 },
      { type: "wealthLevel", value: 1 },
    ],
  },
  {
    id: "gift-of-protection",
    name: "Gift of Protection",
    description: "+1 MP. Gain 3 points to spend on Spells of Protection.",
    unlimited: true,
    effects: [{ type: "mp", value: 1 }],
    spellPoints: { listId: "protection", points: 3 },
  },
  {
    id: "gift-of-sorcery",
    name: "Gift of Sorcery",
    description: "+1 MP. Gain 3 points to spend on Spells of Sorcery.",
    unlimited: true,
    effects: [{ type: "mp", value: 1 }],
    spellPoints: { listId: "sorcery", points: 3 },
  },
  {
    id: "gift-of-vitality",
    name: "Gift of Vitality",
    description:
      "+1 MP, +2 HP (max 8). Once per day per purchase of this gift, you may gain 1 HP instead of 1 MP from a Patron Challenge.",
    full_description: [
      {
        type: "paragraph",
        text: "You gain +1 MP, +2 HP, and once per day you can choose to regain 1 HP instead of 1 MP whenever you complete a Patron Challenge.",
      },
      {
        type: "paragraph",
        text: "For each time this Gift is taken again, you may use this ability another time per day. You cannot exceed the maximum HP score of 8.",
      },
    ],
    unlimited: true,
    effects: [
      { type: "mp", value: 1 },
      { type: "hp", value: 2 },
    ],
  },
  {
    id: "gift-of-war-magic",
    name: "Gift of War Magic",
    description: "+1 MP. Gain 3 points to spend on Spells of War Magic.",
    unlimited: true,
    effects: [{ type: "mp", value: 1 }],
    spellPoints: { listId: "warMagic", points: 3 },
  },
  // Limited gifts (repeatable: false)
  {
    id: "gift-of-alchemy",
    name: "Gift of Alchemy",
    description:
      "+1 MP. You can craft potions: Healing (red, +2 HP), Magic (blue, +2 MP), Poison (green, causes Poisoned condition), Explosive Bomb (deals 1 damage in 2 metre radius). Each requires ingredients and roleplay.",
    full_description: [
      {
        type: "paragraph",
        text: "You gain +1 MP and the ability to craft potions.",
      },
      {
        type: "paragraph",
        text: "By spending at least 1 minute enacting a ritual with appropriate alchemical instruments, you may spend 1 MP and 1 silver coin to create any 3 potions of your choice. These potions can be used by anyone, on themselves or someone else, instantly having its effect. Potions may only be used once.",
      },
      {
        type: "table",
        headers: ["Potion", "Effect", "Description"],
        rows: [
          ["Healing Potion", "+2 HP (up to max).", "Red liquid"],
          ["Magic Potion", "+2 MP (up to max).", "Blue liquid"],
          [
            "Poison",
            'When applied to a weapon, a green ribbon is wound around the weapon’s blade. Once the poison is applied in this way, it lasts for 1 hour. See "Poison".',
            "Green liquid",
          ],
          [
            "Explosive Bomb",
            'When thrown, and the word "Bomb!" is loudly and clearly called, all creatures in a 2 metre radius take 1 magical damage and are knocked back one step.',
            "LARP-safe projectile with a fuse",
          ],
        ],
      },
    ],
    unlimited: false,
    effects: [
      { type: "mp", value: 1 },
      {
        type: "special",
        description:
          "Can craft potions (Healing +2HP red, Magic +2MP blue, Poison green, Explosive Bomb)",
      },
    ],
  },
  {
    id: "gift-of-artifice",
    name: "Gift of Artifice",
    description:
      "+1 MP. You can craft magic items by imbuing spells from the Sorcery or Protection lists (costs 1 MP + 1 gold; item is usable once per day and must be recharged with 1 HP).",
    full_description: [
      {
        type: "paragraph",
        text: "You gain +1 MP and the ability to craft magic items.",
      },
      {
        type: "paragraph",
        text: "You are able to imbue mundane items, such as pendants, books, daggers, or crowns, with magical properties. Trying to enchant an item that already has a magical effect on it will cause the ritual to automatically fail.",
      },
      {
        type: "paragraph",
        text: "By spending at least 1 minute enacting a ritual over the item with appropriate tools, you can spend MP and 1 gold coin to imbue it with a spell from the Spells of Sorcery or Protection that you know.",
      },
      {
        type: "paragraph",
        text: "If another Warlock is present while you perform the ritual, they can spend their own MP to cast a spell they know into the item as you create it.",
      },
      {
        type: "paragraph",
        text: "Spells can be stored in magic items at high levels. so, an enchanted amulet could contain Resist at level 3, or an Invisibility that lasts up to 5 minutes.",
      },
      {
        type: "paragraph",
        text: "The enchanted item can now be used cast that spell, once per day. However, the item requires life force to activate: the caster must sacrifice 1 HP to channel the magic within!",
      },
      {
        type: "paragraph",
        text: "Ensure the item is clearly labelled with its enchantment. GMs may rule that an unlabelled item simply does not function!",
      },
    ],
    unlimited: false,
    effects: [
      { type: "mp", value: 1 },
      {
        type: "special",
        description:
          "Can craft magic items by imbuing known Sorcery/Protection spells (1 MP + 1 gold, usable once per day, must be recharged with 1 HP)",
      },
    ],
  },
  {
    id: "gift-of-the-familiar",
    name: "Gift of the Familiar",
    description:
      "+1 HP, +1 MP, and 1 point to spend on any spell. You have a familiar: a small creature visibly represented on your costume. If the familiar is hit, you lose the HP. You can project your consciousness into it using a blue headband.",
    full_description: [
      {
        type: "paragraph",
        text: "You gain the service of a Familiar, which grants you +1 HP, +1 MP, and gives you 1 point to spend on any spell. This represents magic the Familiar itself knows!",
      },
      {
        type: "paragraph",
        text: "The Familiar takes the form of a small creature of your choice, that must be represented on your character and clearly visible. Examples can include a cat, rat, owl, octopus, pseudodragon, fairy, dismembered crawling hand, mushroom with eyeballs, creepy porcelain doll, or even a platypus.",
      },
      {
        type: "paragraph",
        text: "Your Familiar is part of you, and you share HP - if it gets hit, you lose HP. Familiars are defenceless, and cannot speak or cast spells.",
      },
      {
        type: "paragraph",
        text: "It is usually impossible for a Familiar to leave its master’s side - except through a special ritual. You may project your senses into your Familiar, and send it off by itself. While your Familiar does this, your body is inert and vulnerable.",
      },
      {
        type: "paragraph",
        text: "Leave a clearly labelled mannequin or marker in the spot where your body is. Then, place a clearly visible red headband around your head as you astrally project your consciousness into your Familiar. You can now see and hear everything your Familiar can, but you cannot speak or physically interact with anyone or anything.",
      },
      {
        type: "paragraph",
        text: "You may then puppeteer your Familiar through the physical world as you wish. It should behave in a manner reasonable to its form - for example, a giant spider familiar could climb, and a raven could fly.",
      },
      {
        type: "paragraph",
        text: "To return to your true body, your Familiar must physically make contact with the marker you left behind. You may then instantly return to your physical form.",
      },
    ],
    unlimited: false,
    effects: [
      { type: "hp", value: 1 },
      { type: "mp", value: 1 },
    ],
    spellPoints: { listId: "any", points: 1 },
  },
  {
    id: "gift-of-the-forge",
    name: "Gift of the Forge",
    description:
      "+1 MP. You can craft magic weapons (+1 damage for 1 hour, 1 MP + 1 silver). You can also craft special metal weapons overnight for 1 gold: Silver (white ribbon, +1 damage to Undead), Iron (black/brown ribbon, +1 damage to Fey), Ghost Blade (blue ribbon, can enter Ghost World).",
    full_description: [
      {
        type: "paragraph",
        text: "You gain +1 MP and the ability to craft magical weapons.",
      },
      {
        type: "paragraph",
        text: "By spending at least 1 minute enacting a ritual over the item, you can spend 1 MP and a silver coin to imbue it with magic that lasts for 1 hour.",
      },
      {
        type: "paragraph",
        text: "Magic Weapon: the target weapon deals +1 damage",
      },
      {
        type: "paragraph",
        text: "In addition, you are able to craft items from metals with special properties. If you are in possession of a weapon overnight, have access to a suitable forge, and pay 1 gold coin for materials, you can craft one of the following special weapons:",
      },
      {
        type: "paragraph",
        text: "Silver: the target weapon deals +1 damage to Undead. This is represented by winding a white or silver ribbon around the weapon’s blade / barrel.",
      },
      {
        type: "paragraph",
        text: "Iron: the target weapon deals +1 damage to Fey. This is represented by winding a black or brown ribbon around the weapon’s blade / barrel.",
      },
      {
        type: "paragraph",
        text: "Ghost Blade: the target weapon can be taken into the Ghost World. This is represented by winding a bright blue ribbon around the weapon’s blade / barrel. The full rules for the Ghost World can be found in the Game Rules.",
      },
      {
        type: "paragraph",
        text: "These bonuses cannot be stacked onto the same weapon! A blade cannot be imbued with both Silver and Iron, and as both those metals are anathema to magic, they cannot be Ghostly.",
      },
      {
        type: "paragraph",
        text: "A weapon forged in this way can be reforged, by going through the process again. The reforged weapon loses its previous bonus, which is replaced by the new one.",
      },
    ],
    unlimited: false,
    effects: [
      { type: "mp", value: 1 },
      {
        type: "special",
        description:
          "Can craft magic weapons (+1 damage, 1hr), and special metals: Silver (white ribbon, +1 to Undead), Iron (black ribbon, +1 to Fey), Ghost Blade (blue ribbon, goes to Ghost World). 1 MP + 1 silver for temp magic, 1 gold overnight for metal crafting.",
      },
    ],
  },
  {
    id: "gift-of-life-everlasting",
    name: "Gift of Life Everlasting",
    description:
      "+1 MP. You can craft a Phylactery (a precious item holding part of your soul). Your Dark Offerings limit drops from 8 to 7. If you die while your Phylactery exists, you return to life after 10 minutes at the Phylactery's location as an Undead creature (immune to healing, +1 damage from silver weapons). To destroy the Phylactery requires a ritual, 1 gold, and the speaker must know your true name.",
    full_description: [
      {
        type: "paragraph",
        text: "You gain +1 MP, and the ability to craft a Phylactery to contain a piece of your soul.",
      },
      {
        type: "paragraph",
        text: "You must allocate a precious item to be your Phylactery. You can transfer a part of your soul into this item if you are in possession of it overnight, and pay 1 gold coin for materials to complete the unholy ritual required. In addition, your Dark Offerings limit drops from 8 to 7 - you now have less of your soul available to give away!",
      },
      {
        type: "paragraph",
        text: "You may only have one Phylactery at once. You can repeat the ritual to transfer your soul from one item to another if you wish to change your Phylactery.",
      },
      {
        type: "paragraph",
        text: "If you have a Phylactery when you drop to 0 HP and Bleed Out, ignore the normal rules for dying. Instead of petitioning a Patron for resurrection, you may return to life after 10 minutes with 1 HP at your Phylactery’s location, but your creature type is now Undead, and you cannot abide the presence of silver, or wield silver weapons.",
      },
      {
        type: "paragraph",
        text: "If you are unable to locate your Phylactery after 1 hour, however, and have not sought a Patron's help... you die!",
      },
      {
        type: "paragraph",
        text: "A Phylactery can be destroyed by a character with the Gift of Life Everlasting by a similar ritual used to create one - spending a night in possession of it, and using up 1 gold piece. In addition, the caster must speak the creator’s true name, or the Phylactery is not destroyed. If you destroy your own Phylactery, you can remove the Undead attribute from yourself if you have it, and your Dark Offerings limit rises back to 8.",
      },
    ],
    unlimited: false,
    effects: [
      { type: "mp", value: 1 },
      {
        type: "special",
        description:
          "Craft a Phylactery: return as Undead after death (10 min, 1 gold cost). Dark Offerings limit reduced from 8 to 7. Undead: immune to healing, vulnerable to silver (+1 damage).",
      },
    ],
  },
];
